import { useEffect, useState } from "react";
import type { CallPreferenceSettings } from "@dayvise/client-sdk";
import { Badge, Button, Card, MUTED_TEXT, Select, Spinner, Switch, TextField, rowStyle, stackStyle } from "@dayvise/react-ui";
import { useCallPreferenceMutations, useCallPreferences, usePresenceLeases } from "./hooks.ts";

const HOURS = Array.from({ length: 24 }, (_entry, hour) => ({ value: String(hour), label: `${String(hour).padStart(2, "0")}:00` }));

interface Draft {
  readonly callEnabled: boolean;
  readonly preferredDeviceIds: readonly string[];
  readonly quietStartHour: number | null;
  readonly quietEndHour: number | null;
  readonly quietTimezoneOffsetMinutes: number | null;
  readonly snoozeUntil: string | null;
}

function draftOf(preferences: CallPreferenceSettings): Draft {
  return {
    callEnabled: preferences.callEnabled,
    preferredDeviceIds: preferences.preferredDeviceIds,
    quietStartHour: preferences.quietStartHour,
    quietEndHour: preferences.quietEndHour,
    quietTimezoneOffsetMinutes: preferences.quietTimezoneOffsetMinutes,
    snoozeUntil: preferences.snoozeUntil,
  };
}

function draftProblem(draft: Draft): string | null {
  for (const hour of [draft.quietStartHour, draft.quietEndHour]) {
    if (hour !== null && (!Number.isInteger(hour) || hour < 0 || hour > 23)) return "quiet hours must be whole hours between 00:00 and 23:00";
  }
  if ((draft.quietStartHour === null) !== (draft.quietEndHour === null)) return "set both quiet-hour boundaries, or neither";
  const offset = draft.quietTimezoneOffsetMinutes;
  if (offset !== null && (!Number.isInteger(offset) || offset < -720 || offset > 840)) return "the quiet-hours timezone offset must be between -720 and 840 minutes";
  if (draft.quietStartHour !== null && offset === null) return "quiet hours need the recipient's timezone offset";
  if (draft.snoozeUntil !== null && !Number.isFinite(Date.parse(draft.snoozeUntil))) return "the snooze end must be a timestamp";
  return null;
}

function snoozeState(until: string | null): string {
  if (until === null) return "Not snoozed";
  const at = Date.parse(until);
  if (!Number.isFinite(at)) return "Not snoozed";
  return at > Date.now() ? `Snoozed until ${new Date(at).toISOString()}` : `Snooze expiry passed (${new Date(at).toISOString()})`;
}

/**
 * Call-me preferences: who may be rung, in what order, and when not to ring.
 *
 * Presence routing reads exactly this record, so the panel states what the settings
 * do and what the deployment can actually deliver rather than promising a call.
 */
export function CallPreferencesPanel() {
  const query = useCallPreferences();
  const leases = usePresenceLeases();
  const mutations = useCallPreferenceMutations();
  const [draft, setDraftState] = useState<Draft | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const loaded = query.data?.preferences;

  useEffect(() => {
    if (loaded !== undefined) setDraftState(draftOf(loaded));
  }, [loaded]);

  const error = query.error ?? mutations.update.error ?? mutations.reset.error;
  const devices = (leases.data?.leases ?? []).filter((lease) => lease.callCapable);
  const set = (change: Partial<Draft>) => setDraftState((previous) => (previous === null ? previous : { ...previous, ...change }));

  return (
    <Card
      title="Call me when"
      subtitle="Presence routing decides whether an input request pops up, rings a device, or waits in the inbox; these are the recipient's own rules."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        {query.isPending || draft === null ? <Spinner label="Loading call preferences" /> : null}
        {error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{error instanceof Error ? error.message : "Could not read or save call preferences"}</p> : null}
        {problem !== null ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{problem}</p> : null}
        {draft !== null ? (
          <>
            <Switch
              label={draft.callEnabled ? "Calls enabled" : "Calls disabled"}
              description={draft.callEnabled
                ? "An idle call-capable device may be rung when nothing is fresh."
                : "Requests stay in the inbox and never ring a device."}
              checked={draft.callEnabled}
              disabled={mutations.update.isPending}
              onChange={(checked) => set({ callEnabled: checked })}
            />
            <div style={rowStyle("var(--dayvise-space-xs)")}>
              <Badge tone={draft.snoozeUntil !== null && Date.parse(draft.snoozeUntil) > Date.now() ? "warning" : "neutral"}>{snoozeState(draft.snoozeUntil)}</Badge>
              {draft.snoozeUntil !== null ? <Button size="sm" variant="ghost" disabled={mutations.update.isPending} onClick={() => set({ snoozeUntil: null })}>Clear snooze</Button> : null}
            </div>
            <TextField
              label="Snooze until (optional)"
              placeholder="2026-10-05T09:00"
              value={draft.snoozeUntil === null ? "" : draft.snoozeUntil.slice(0, 16)}
              disabled={mutations.update.isPending}
              onChange={(event) => set({ snoozeUntil: event.target.value.trim().length === 0 ? null : new Date(event.target.value).toISOString() })}
              hint="While snoozed, nothing rings; the request stays pending and is re-evaluated when the snooze ends."
            />
            <div style={rowStyle("var(--dayvise-space-sm)")}>
              <Select
                label="Quiet hours start"
                value={draft.quietStartHour === null ? "off" : String(draft.quietStartHour)}
                disabled={mutations.update.isPending}
                options={[{ value: "off", label: "No quiet hours" }, ...HOURS]}
                onChange={(event) => set({ quietStartHour: event.target.value === "off" ? null : Number(event.target.value), quietEndHour: event.target.value === "off" ? null : draft.quietEndHour })}
              />
              <Select
                label="Quiet hours end"
                value={draft.quietEndHour === null ? "off" : String(draft.quietEndHour)}
                disabled={mutations.update.isPending || draft.quietStartHour === null}
                options={[{ value: "off", label: "No quiet hours" }, ...HOURS]}
                onChange={(event) => set({ quietEndHour: event.target.value === "off" ? null : Number(event.target.value) })}
              />
              <TextField
                label="Timezone offset (minutes from UTC)"
                type="number"
                value={draft.quietTimezoneOffsetMinutes === null ? "" : String(draft.quietTimezoneOffsetMinutes)}
                disabled={mutations.update.isPending || draft.quietStartHour === null}
                onChange={(event) => set({ quietTimezoneOffsetMinutes: event.target.value.trim().length === 0 ? null : Number(event.target.value) })}
              />
            </div>
            <div style={stackStyle("var(--dayvise-space-xs)")}>
              <span style={{ ...MUTED_TEXT, margin: 0 }}>
                {devices.length === 0
                  ? "No call-capable device is currently registered; ordering applies as soon as one reports itself."
                  : "Preferred order (first choice first); a device that is not listed is still eligible after these."}
              </span>
              {devices.map((lease) => (
                <Switch
                  key={lease.deviceId}
                  label={`${lease.deviceId} (${lease.state})`}
                  description={draft.preferredDeviceIds.includes(lease.deviceId) ? "Preferred" : "Eligible, not preferred"}
                  checked={draft.preferredDeviceIds.includes(lease.deviceId)}
                  disabled={mutations.update.isPending}
                  onChange={(checked) => set({
                    preferredDeviceIds: checked
                      ? [...draft.preferredDeviceIds, lease.deviceId]
                      : draft.preferredDeviceIds.filter((deviceId) => deviceId !== lease.deviceId),
                  })}
                />
              ))}
            </div>
            <p style={{ ...MUTED_TEXT, margin: 0 }}>
              These rules are enforced by the platform's routing decision, which also honours a live foreground session and never rings while activity is fresh. A ringing call additionally needs a call service in this deployment; notifications and popups work without one.
            </p>
            <div style={rowStyle("var(--dayvise-space-xs)")}>
              <Button
                variant="primary"
                size="sm"
                loading={mutations.update.isPending}
                onClick={() => {
                  const found = draftProblem(draft);
                  setProblem(found);
                  if (found !== null) return;
                  mutations.update.mutate(draft);
                }}
              >
                Save preferences
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={mutations.reset.isPending}
                onClick={() => {
                  setProblem(null);
                  mutations.reset.mutate(undefined, { onSuccess: () => setDraftState(draftOf({ callEnabled: true, preferredDeviceIds: [], quietStartHour: null, quietEndHour: null, quietTimezoneOffsetMinutes: null, snoozeUntil: null, updatedAt: null })) });
                }}
              >
                Reset to defaults
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </Card>
  );
}
